/**
 * Assignment Status Calculator V2
 *
 * Calculates assignment status based on the 3-track status system.
 * Handles automatic transitions and status synchronization.
 */

import { isToday, isPast, parseISO } from 'date-fns'
import type {
  AssignmentStatusV2,
  TgWorkStatus,
  CustomerCommStatus,
  CustomerInvoiceStatus,
  TgInvoiceStatus
} from '../types/statusTypes'
import { allTgsReported, allTgsApproved } from './tgStatusCalculator'

// =============================================
// TYPES
// =============================================

export interface AssignmentDataV2 {
  status: AssignmentStatusV2
  start_date: string | null
  start_time: string | null
  subcontractor_ids: string[]

  // Customer status (optional - only needed for slutford transition)
  customer_comm_status?: CustomerCommStatus
  customer_invoice_status?: CustomerInvoiceStatus

  // For checking if all TG invoices received
  tg_statuses?: TgWorkStatus[]
  tg_invoice_statuses?: TgInvoiceStatus[]
}

// =============================================
// ASSIGNMENT STATUS CALCULATION
// =============================================

/**
 * Calculate assignment status based on current data.
 *
 * Status flow:
 * ohanterad -> planering -> preliminar -> planerad -> pagaende -> granskning -> attesterad -> slutford
 *                                                                                          \-> avbruten
 */
export function calculateAssignmentStatusV2(
  assignment: AssignmentDataV2
): AssignmentStatusV2 {
  const { status, start_date, start_time, subcontractor_ids } = assignment

  // Terminal states - don't auto-calculate
  if (status === 'slutford' || status === 'avbruten') {
    return status
  }

  // Check for slutford: customer invoiced AND all TG invoices received
  if (status === 'attesterad') {
    if (canTransitionToSlutford(assignment)) {
      return 'slutford'
    }
    return 'attesterad'
  }

  // Check for granskning: all TGs have reported
  if (status === 'pagaende' || status === 'granskning') {
    if (assignment.tg_statuses && allTgsReported(assignment.tg_statuses)) {
      return 'granskning'
    }
    if (status === 'granskning') {
      return 'granskning' // Keep if already in granskning
    }
    return 'pagaende'
  }

  // Check for pagaende: assignment date has occurred
  if (status === 'planerad') {
    if (isAssignmentDateReached(start_date)) {
      return 'pagaende'
    }
    return 'planerad'
  }

  // Calculate based on presence of TG and date
  const hasTg = subcontractor_ids && subcontractor_ids.length > 0
  const hasDateTime = Boolean(start_date && start_time)

  // Both TG and date exist -> preliminar
  if (hasTg && hasDateTime) {
    return 'preliminar'
  }

  // Either TG or date exists -> planering
  if (hasTg || hasDateTime || start_date) {
    return 'planering'
  }

  // Nothing -> ohanterad
  return 'ohanterad'
}

/**
 * Check if assignment date has been reached (today or past)
 */
function isAssignmentDateReached(startDate: string | null): boolean {
  if (!startDate) return false

  try {
    const date = parseISO(startDate)
    return isToday(date) || isPast(date)
  } catch {
    return false
  }
}

/**
 * Check if assignment can transition to slutford
 */
function canTransitionToSlutford(assignment: AssignmentDataV2): boolean {
  // Customer must be invoiced
  if (assignment.customer_invoice_status !== 'fakturerad') {
    return false
  }

  // All TG invoices must be received
  if (assignment.tg_invoice_statuses) {
    const allReceived = assignment.tg_invoice_statuses.every(
      status => status === 'faktura_mottagen'
    )
    if (!allReceived) {
      return false
    }
  }

  return true
}

// =============================================
// STATUS TRANSITION VALIDATION
// =============================================

/**
 * Get valid manual status transitions
 */
export function getValidAssignmentTransitions(
  currentStatus: AssignmentStatusV2
): AssignmentStatusV2[] {
  const transitions: Record<AssignmentStatusV2, AssignmentStatusV2[]> = {
    'ohanterad': ['planering', 'avbruten'],
    'planering': ['preliminar', 'avbruten'],
    'preliminar': ['planerad', 'planering', 'avbruten'],
    'planerad': ['preliminar', 'pagaende', 'avbruten'],
    'pagaende': ['granskning', 'avbruten'],
    'granskning': ['attesterad', 'pagaende', 'avbruten'],
    'attesterad': ['slutford', 'granskning', 'avbruten'],
    'slutford': ['attesterad'], // Can unlock
    'avbruten': ['ohanterad']   // Can reactivate
  }

  return transitions[currentStatus] || []
}

/**
 * Check if manual transition is allowed
 */
export function canTransitionAssignmentStatus(
  currentStatus: AssignmentStatusV2,
  targetStatus: AssignmentStatusV2
): boolean {
  const validTransitions = getValidAssignmentTransitions(currentStatus)
  return validTransitions.includes(targetStatus)
}

/**
 * Check if status requires confirmation before transitioning
 */
export function requiresConfirmation(
  _currentStatus: AssignmentStatusV2,
  targetStatus: AssignmentStatusV2,
  customerCommStatus: CustomerCommStatus
): { required: boolean; reason?: string } {
  // Confirm planerad without customer confirmation
  if (targetStatus === 'planerad' && customerCommStatus !== 'bekraftad') {
    return {
      required: true,
      reason: 'Kunden har inte bekräftat uppdraget'
    }
  }

  return { required: false }
}

// =============================================
// TG STATUS SYNC
// =============================================

/**
 * Get updated TG statuses when assignment status changes
 */
export function syncTgStatusesOnAssignmentChange(
  oldStatus: AssignmentStatusV2,
  newStatus: AssignmentStatusV2,
  currentTgStatuses: TgWorkStatus[]
): TgWorkStatus[] {
  // When assignment becomes planerad, update TGs from preliminar to tilldelad
  if (newStatus === 'planerad' && oldStatus !== 'planerad') {
    return currentTgStatuses.map(status =>
      status === 'preliminar_tilldelad' ? 'tilldelad' : status
    )
  }

  // When assignment becomes pagaende, update TGs from tilldelad to ej_rapporterad
  if (newStatus === 'pagaende' && oldStatus !== 'pagaende') {
    return currentTgStatuses.map(status =>
      status === 'tilldelad' ? 'ej_rapporterad' : status
    )
  }

  // When assignment goes back to preliminar, reset TG statuses
  if (newStatus === 'preliminar' && oldStatus === 'planerad') {
    return currentTgStatuses.map(status =>
      status === 'tilldelad' ? 'preliminar_tilldelad' : status
    )
  }

  return currentTgStatuses
}

// =============================================
// ASSIGNMENT STATUS FROM TG CHANGES
// =============================================

/**
 * Check if assignment status should change based on TG status changes
 */
export function shouldUpdateAssignmentStatusFromTg(
  assignmentStatus: AssignmentStatusV2,
  tgStatuses: TgWorkStatus[]
): AssignmentStatusV2 | null {
  // If pagaende and all TGs reported -> granskning
  if (assignmentStatus === 'pagaende' && allTgsReported(tgStatuses)) {
    return 'granskning'
  }

  // If granskning and all TGs approved -> attesterad
  if (assignmentStatus === 'granskning' && allTgsApproved(tgStatuses)) {
    return 'attesterad'
  }

  // If granskning but not all reported anymore -> back to pagaende
  if (assignmentStatus === 'granskning' && !allTgsReported(tgStatuses)) {
    return 'pagaende'
  }

  return null
}

// =============================================
// LOCKED STATUS HELPERS
// =============================================

/**
 * Check if assignment is in a locked state
 */
export function isAssignmentLocked(status: AssignmentStatusV2): boolean {
  return status === 'slutford'
}

/**
 * Check if assignment can be edited
 */
export function canEditAssignment(status: AssignmentStatusV2): boolean {
  return !isAssignmentLocked(status)
}

// =============================================
// STATUS SUMMARY
// =============================================

export interface AssignmentStatusSummary {
  assignmentStatus: AssignmentStatusV2
  customerCommStatus: CustomerCommStatus
  customerInvoiceStatus: CustomerInvoiceStatus
  tgCount: number
  tgReportedCount: number
  tgApprovedCount: number
  tgInvoiceReceivedCount: number
  isComplete: boolean
  nextActions: string[]
}

/**
 * Get a summary of all status tracks for an assignment
 */
export function getAssignmentStatusSummary(
  assignment: AssignmentDataV2
): AssignmentStatusSummary {
  const tgStatuses = assignment.tg_statuses || []
  const tgInvoiceStatuses = assignment.tg_invoice_statuses || []

  const tgCount = tgStatuses.length
  const tgReportedCount = tgStatuses.filter(s =>
    s === 'rapporterad' || s === 'godkand'
  ).length
  const tgApprovedCount = tgStatuses.filter(s => s === 'godkand').length
  const tgInvoiceReceivedCount = tgInvoiceStatuses.filter(
    s => s === 'faktura_mottagen'
  ).length

  const isComplete = assignment.status === 'slutford'

  // Determine next actions
  const nextActions: string[] = []

  if (assignment.status === 'preliminar') {
    if (assignment.customer_comm_status !== 'bekraftad') {
      nextActions.push('Bekräfta med kund')
    }
    nextActions.push('Bekräfta planering')
  }

  if (assignment.status === 'pagaende' && tgReportedCount < tgCount) {
    nextActions.push(`Väntar på ${tgCount - tgReportedCount} rapporter`)
  }

  if (assignment.status === 'granskning') {
    nextActions.push('Attestera rapporter')
  }

  if (assignment.status === 'attesterad') {
    if (assignment.customer_invoice_status !== 'fakturerad') {
      nextActions.push('Fakturera kund')
    }
    if (tgInvoiceReceivedCount < tgCount) {
      nextActions.push(`Väntar på ${tgCount - tgInvoiceReceivedCount} TG-fakturor`)
    }
  }

  return {
    assignmentStatus: assignment.status,
    customerCommStatus: assignment.customer_comm_status || 'ej_kontaktad',
    customerInvoiceStatus: assignment.customer_invoice_status || 'ej_fakturerad',
    tgCount,
    tgReportedCount,
    tgApprovedCount,
    tgInvoiceReceivedCount,
    isComplete,
    nextActions
  }
}
